import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Autocomplete,
  FormControlLabel,
  FormGroup,
  Checkbox,
  IconButton
} from '@mui/material';
import {
  School as SchoolIcon,
  Group as GroupIcon,
  Save as SaveIcon,
  FileDownload as DownloadIcon,
  CheckCircle as OpenIcon,
  Lock as ClosedIcon,
  Person as PersonIcon,
  Assignment as SurveyIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../services/api';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_STUDENT_MAJORS } from '../constants/majors';

const MAJORS = DEFAULT_STUDENT_MAJORS;

export const SCHOOL_SIZES = [
  { value: 'PRIMARY_SMALL', label: '1. โรงเรียนระดับประถมศึกษาขนาดเล็ก (โควตาไม่เกิน 3 คน)', shortLabel: 'ประถมขนาดเล็ก', quota: 3 },
  { value: 'PRIMARY_MEDIUM', label: '2. โรงเรียนระดับประถมศึกษาขนาดกลาง (โควตาไม่เกิน 5 คน)', shortLabel: 'ประถมขนาดกลาง', quota: 5 },
  { value: 'PRIMARY_LARGE', label: '3. โรงเรียนระดับประถมศึกษาขนาดใหญ่ (โควตาไม่เกิน 10 คน)', shortLabel: 'ประถมขนาดใหญ่', quota: 10 },
  { value: 'EXPANDED', label: '4. โรงเรียนขยายโอกาส / มัธยมศึกษาขนาดเล็ก (โควตาไม่เกิน 10 คน)', shortLabel: 'ขยายโอกาส/มัธยมขนาดเล็ก', quota: 10 },
  { value: 'SECONDARY_MEDIUM_LARGE', label: '5. โรงเรียนระดับมัธยมศึกษาขนาดกลาง - ใหญ่ (โควตาไม่เกิน 15 คน)', shortLabel: 'มัธยมศึกษาขนาดกลาง-ใหญ่', quota: 15 }
];

const EDUCATION_LEVEL_OPTIONS = [
  'อนุบาล - ชั้นประถมศึกษาปีที่ 6',
  'อนุบาล - ชั้นมัธยมศึกษาปีที่ 3',
  'ชั้นประถมศึกษาปีที่ 1 - 6',
  'ชั้นประถมศึกษาปีที่ 1 - ชั้นมัธยมศึกษาปีที่ 3',
  'ชั้นมัธยมศึกษาปีที่ 1 - 3',
  'ชั้นมัธยมศึกษาปีที่ 1 - 6'
];

const ONESQA_RATINGS = [
  'ดีมาก',
  'ดี',
  'พอใช้',
  'ปรับปรุง',
  'ไม่เปิดสอน / ไม่ได้รับการประเมิน'
];

const formatThaiDateTime = (isoStr: string | null) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })} เวลา ${d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
};

const StudentDemandSurvey = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isCoTeacher = user.role === 'CO_TEACHER';
  const isAdminOrStaff = ['ADMIN', 'STAFF'].includes(user.role);
  const navigate = useNavigate();

  // Tabs for Admin/Staff: 0 = กรอกความต้องการรายโรงเรียน, 1 = ภาพรวมสรุปทุกโรงเรียน
  const [activeTab, setActiveTab] = useState(0);

  // Sub-tabs for Form: 0 = ความต้องการนักศึกษาตามสาขา, 1 = ข้อมูลความพร้อมและบริบทสถานศึกษา
  const [formSubTab, setFormSubTab] = useState(0);

  // Filter & term state
  const [selectedSemester, setSelectedSemester] = useState('1');
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear() + 543));
  const [isOpen, setIsOpen] = useState(true);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // School data (for Co-Teacher or selected school by Admin)
  const [school, setSchool] = useState<any>(null);
  const [allSchools, setAllSchools] = useState<any[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');

  // 1. Demand & Contact Form inputs
  const [demands, setDemands] = useState<Record<string, number>>({});
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submittedByName, setSubmittedByName] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  // 2. School Profile & Readiness inputs (Extracted from Word Doc)
  const [hasPreviousExperience, setHasPreviousExperience] = useState('เคย');
  const [lastParticipatedYear, setLastParticipatedYear] = useState('');
  const [schoolSizeLevel, setSchoolSizeLevel] = useState('');
  const [distanceFromUniversityKm, setDistanceFromUniversityKm] = useState<string | number>('');
  const [hasAccommodation, setHasAccommodation] = useState(0);
  const [accommodationDetails, setAccommodationDetails] = useState('');
  const [hasLunch, setHasLunch] = useState(0);
  const [welfareNotes, setWelfareNotes] = useState('');
  const [teacherCountTotal, setTeacherCountTotal] = useState<string | number>('');
  const [teacherCountMale, setTeacherCountMale] = useState<string | number>('');
  const [teacherCountFemale, setTeacherCountFemale] = useState<string | number>('');
  const [studentCountTotal, setStudentCountTotal] = useState<string | number>('');
  const [educationLevels, setEducationLevels] = useState<string[]>([]);
  const [onesqaRound, setOnesqaRound] = useState('รอบที่ 4');
  const [onesqaEarlyChildhood, setOnesqaEarlyChildhood] = useState('');
  const [onesqaPrimary, setOnesqaPrimary] = useState('');
  const [onesqaSecondary, setOnesqaSecondary] = useState('');
  const [mentorList, setMentorList] = useState<Array<{
    name: string;
    degree: string;
    academic_standing: string;
    appointed_date: string;
    experience_years: string;
    subject: string;
    grade_level: string;
  }>>([]);

  // Summary data for Admin/Staff tab
  const [summaryData, setSummaryData] = useState<any[]>([]);

  // Initialize page & load settings
  useEffect(() => {
    const initPage = async () => {
      try {
        setLoading(true);
        const settingsRes = await api.get('/settings');
        const settings = settingsRes.data || {};
        const defYear = settings.current_academic_year ? String(settings.current_academic_year) : String(new Date().getFullYear() + 543);
        const defSem = settings.current_semester ? String(settings.current_semester) : '1';
        setSelectedYear(defYear);
        setSelectedSemester(defSem);

        if (isAdminOrStaff) {
          const schRes = await api.get('/schools');
          setAllSchools(schRes.data || []);
          if (schRes.data && schRes.data.length > 0) {
            setSelectedSchoolId(schRes.data[0].id);
            fetchDemandForSchool(schRes.data[0].id, defYear, defSem);
          }
          fetchSummary(defYear, defSem);
        } else if (isCoTeacher) {
          fetchMyDemand(defYear, defSem);
        }
      } catch (err) {
        console.error('Failed to init demand survey page:', err);
      } finally {
        setLoading(false);
      }
    };
    initPage();
  }, []);

  const populateFormFields = (data: any) => {
    const d = data.demand || {};
    const sch = data.school || {};

    setSchool(sch);
    setIsOpen(Boolean(data.is_open));
    setStartDate(data.start_date || null);
    setEndDate(data.end_date || null);

    const parsedJson = d.demand_json || {};
    const initDemands: Record<string, number> = {};
    MAJORS.forEach(m => {
      initDemands[m] = parseInt(parsedJson[m] ?? parsedJson[m.replace(/\s+/g, '')]) || 0;
    });
    setDemands(initDemands);
    setContactPerson(d.contact_person || '');
    setContactPhone(d.contact_phone || '');
    setNotes(d.notes || '');
    setSubmittedByName(d.submitted_by_name || '');
    setUpdatedAt(d.updated_at || '');

    // Readiness fields (from demand or fallback to school)
    setHasPreviousExperience(d.has_previous_experience || sch.has_previous_experience || 'เคย');
    setLastParticipatedYear(d.last_participated_year || sch.last_participated_year || '');
    setSchoolSizeLevel(d.school_size_level || sch.school_size_level || '');
    setDistanceFromUniversityKm(d.distance_from_university_km ?? sch.distance_from_university_km ?? '');
    setHasAccommodation(d.has_accommodation !== undefined && d.has_accommodation !== null ? Number(d.has_accommodation) : (sch.has_accommodation !== undefined && sch.has_accommodation !== null ? Number(sch.has_accommodation) : 0));
    setAccommodationDetails(d.accommodation_details || sch.accommodation_details || '');
    setHasLunch(d.has_lunch !== undefined && d.has_lunch !== null ? Number(d.has_lunch) : (sch.has_lunch !== undefined && sch.has_lunch !== null ? Number(sch.has_lunch) : 0));
    setWelfareNotes(d.welfare_notes || sch.welfare_notes || '');
    setTeacherCountTotal(d.teacher_count_total ?? sch.teacher_count_total ?? '');
    setTeacherCountMale(d.teacher_count_male ?? sch.teacher_count_male ?? '');
    setTeacherCountFemale(d.teacher_count_female ?? sch.teacher_count_female ?? '');
    setStudentCountTotal(d.student_count_total ?? sch.student_count_total ?? '');

    const edLevels = d.education_levels_json || sch.education_levels_json;
    setEducationLevels(Array.isArray(edLevels) ? edLevels : []);

    const onesqa = d.onesqa_evaluations_json || sch.onesqa_evaluations_json || {};
    setOnesqaRound(onesqa.round || 'รอบที่ 4');
    setOnesqaEarlyChildhood(onesqa.early_childhood || '');
    setOnesqaPrimary(onesqa.primary || '');
    setOnesqaSecondary(onesqa.secondary || '');

    const mentors = d.mentor_readiness_json || sch.mentor_readiness_json;
    setMentorList(Array.isArray(mentors) ? mentors : []);
  };

  // Fetch demand for Co-Teacher's own school
  const fetchMyDemand = async (year = selectedYear, sem = selectedSemester) => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await api.get('/schools/demand/me', {
        params: { academic_year: year, semester: sem }
      });
      populateFormFields(res.data);
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลความต้องการได้' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch demand for a specific school (Admin/Staff)
  const fetchDemandForSchool = async (sId: string, year = selectedYear, sem = selectedSemester) => {
    if (!sId) return;
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await api.get(`/schools/demand/${sId}`, {
        params: { academic_year: year, semester: sem }
      });
      populateFormFields(res.data);
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลความต้องการของโรงเรียนนี้ได้' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary for all schools (Admin/Staff Tab 1)
  const fetchSummary = async (year = selectedYear, sem = selectedSemester) => {
    try {
      const res = await api.get('/schools/demand-summary', {
        params: { academic_year: year, semester: sem }
      });
      setSummaryData(res.data.data || []);
      setIsOpen(Boolean(res.data.is_open));
      setStartDate(res.data.start_date || null);
      setEndDate(res.data.end_date || null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTermChange = (newYear: string, newSem: string) => {
    setSelectedYear(newYear);
    setSelectedSemester(newSem);
    if (isCoTeacher) {
      fetchMyDemand(newYear, newSem);
    } else {
      if (selectedSchoolId) {
        fetchDemandForSchool(selectedSchoolId, newYear, newSem);
      }
      fetchSummary(newYear, newSem);
    }
  };

  const handleMajorCountChange = (major: string, val: string) => {
    const num = Math.max(0, parseInt(val) || 0);
    setDemands(prev => ({
      ...prev,
      [major]: num
    }));
  };

  const handleAddMentorRow = () => {
    setMentorList(prev => [
      ...prev,
      {
        name: '',
        degree: '',
        academic_standing: 'ชำนาญการ',
        appointed_date: '',
        experience_years: '',
        subject: '',
        grade_level: ''
      }
    ]);
  };

  const handleRemoveMentorRow = (index: number) => {
    setMentorList(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleMentorChange = (index: number, field: string, value: string) => {
    setMentorList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const totalDemand = Object.values(demands).reduce((acc, curr) => acc + (curr || 0), 0);
  const currentSchoolSizeObj = SCHOOL_SIZES.find(s => s.value === schoolSizeLevel);
  const recommendedQuota = currentSchoolSizeObj?.quota || 0;
  const isOverRecommendedQuota = recommendedQuota > 0 && totalDemand > recommendedQuota;
  const hasAnyMajorOverQuota = Object.values(demands).some(c => (c || 0) > 2);

  const handleSave = async () => {
    const targetSchoolId = isCoTeacher ? school?.id : selectedSchoolId;
    if (!targetSchoolId) {
      alert('กรุณาเลือกโรงเรียน');
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        school_id: targetSchoolId,
        academic_year: parseInt(selectedYear),
        semester: parseInt(selectedSemester),
        demand_json: demands,
        notes,
        contact_person: contactPerson,
        contact_phone: contactPhone,
        has_previous_experience: hasPreviousExperience,
        last_participated_year: lastParticipatedYear,
        school_size_level: schoolSizeLevel,
        distance_from_university_km: distanceFromUniversityKm !== '' && distanceFromUniversityKm !== null ? parseFloat(String(distanceFromUniversityKm)) : null,
        has_accommodation: hasAccommodation,
        accommodation_details: accommodationDetails,
        has_lunch: hasLunch,
        welfare_notes: welfareNotes,
        teacher_count_total: parseInt(String(teacherCountTotal)) || 0,
        teacher_count_male: parseInt(String(teacherCountMale)) || 0,
        teacher_count_female: parseInt(String(teacherCountFemale)) || 0,
        student_count_total: parseInt(String(studentCountTotal)) || 0,
        education_levels_json: educationLevels,
        onesqa_evaluations_json: {
          round: onesqaRound,
          early_childhood: onesqaEarlyChildhood,
          primary: onesqaPrimary,
          secondary: onesqaSecondary
        },
        mentor_readiness_json: mentorList
      };

      const res = await api.post('/schools/demand', payload);
      setMessage({
        type: 'success',
        text: res.data.message || 'บันทึกข้อมูลความต้องการนักศึกษาและข้อมูลความพร้อมสถานศึกษาเรียบร้อยแล้ว'
      });

      if (isCoTeacher) {
        fetchMyDemand(selectedYear, selectedSemester);
      } else {
        fetchDemandForSchool(targetSchoolId, selectedYear, selectedSemester);
        fetchSummary(selectedYear, selectedSemester);
      }
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'
      });
    } finally {
      setSaving(false);
    }
  };

  // Export summary to Excel
  const handleExportSummaryExcel = () => {
    if (summaryData.length === 0) {
      alert('ไม่มีข้อมูลสำหรับส่งออก');
      return;
    }

    const exportRows = summaryData.map((row, index) => {
      const sizeObj = SCHOOL_SIZES.find(s => s.value === row.school_size_level);
      const rowItem: any = {
        'ที่': index + 1,
        'ชื่อโรงเรียน': row.school_name,
        'สังกัด': row.affiliation || '-',
        'ขนาดสถานศึกษา': sizeObj ? sizeObj.shortLabel : '-',
        'ระยะทางจาก มรภ.กำแพงเพชร (กม.)': row.distance_from_university_km ?? '-',
        'ที่พักสำหรับนักศึกษา': row.has_accommodation ? 'มีที่พัก' : 'ไม่มี',
        'รายละเอียดที่พัก': row.accommodation_details || '-',
        'อาหารกลางวัน': row.has_lunch ? 'มี' : 'ไม่มี',
        'สวัสดิการอื่นๆ': row.welfare_notes || '-',
        'เคยร่วมเป็นเครือข่าย': row.has_previous_experience || '-',
        'ปีที่เคยรับล่าสุด': row.last_participated_year || '-',
        'จำนวนครูรวม (คน)': row.teacher_count_total || 0,
        'จำนวนนักเรียนรวม (คน)': row.student_count_total || 0,
        'สมศ. ปฐมวัย': row.onesqa_evaluations_json?.early_childhood || '-',
        'สมศ. ประถม': row.onesqa_evaluations_json?.primary || '-',
        'สมศ. มัธยม': row.onesqa_evaluations_json?.secondary || '-',
        'จำนวนครูพี่เลี้ยงที่พร้อมรับ (คน)': Array.isArray(row.mentor_readiness_json) ? row.mentor_readiness_json.length : 0,
        'ผู้ประสานงาน/ครูพี่เลี้ยง': row.contact_person || row.submitted_by_name || '-',
        'เบอร์โทรศัพท์': row.contact_phone || '-',
        'ยอดรวมความต้องการ (คน)': row.effective_total || 0,
      };

      // Add each major column
      MAJORS.forEach(m => {
        rowItem[m] = row.demand_json?.[m] ?? row.demand_json?.[m.replace(/\s+/g, '')] ?? 0;
      });

      rowItem['หมายเหตุ'] = row.notes || '';
      rowItem['สถานะการส่งข้อมูล'] = row.has_submitted ? 'ส่งข้อมูลแล้ว' : 'ยังไม่ส่ง (ใช้ค่าเริ่มต้น)';
      return rowItem;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'StudentDemand');
    XLSX.writeFile(workbook, `tpms_student_demand_${selectedSemester}_${selectedYear}.xlsx`);
  };

  if (isCoTeacher && !isOpen && !loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 650, mx: 'auto' }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <ClosedIcon color="disabled" sx={{ fontSize: 60, mb: 1.5 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            ขณะนี้ระบบสำรวจความต้องการนักศึกษาฝึกประสบการณ์ปิดรับข้อมูลแล้ว
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {endDate && new Date() > new Date(endDate) ? `สิ้นสุดระยะเวลาการรับข้อมูลเมื่อ ${formatThaiDateTime(endDate)}` : ''}
            {startDate && new Date() < new Date(startDate) ? `จะเริ่มเปิดรับข้อมูลในวันที่ ${formatThaiDateTime(startDate)}` : ''}
            {(!startDate && !endDate) || (startDate && new Date() >= new Date(startDate) && endDate && new Date() <= new Date(endDate)) ? 'ระบบถูกปิดโดยฝ่ายฝึกประสบการณ์วิชาชีพครู' : ''}
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }}>
            หากสถานศึกษาของท่านมีความประสงค์จำเป็นในการระบุหรือปรับเปลี่ยนจำนวนนักศึกษา โปรดติดต่อฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
          </Alert>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            กลับหน้าหลัก
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1250, mx: 'auto', p: { xs: 1, sm: 2 } }}>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        gap: 2, 
        mb: 3 
      }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            <SurveyIcon color="primary" sx={{ fontSize: { xs: 28, sm: 34 } }} /> 
            สำรวจความต้องการนักศึกษาฝึกประสบการณ์ (Student Teacher Demand Survey)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            แบบฟอร์มแสดงความจำนงการเข้าร่วมเป็นเครือข่ายโรงเรียนฝึกประสบการณ์วิชาชีพครู และระบุจำนวนนักศึกษาตามเกณฑ์
          </Typography>
        </Box>

        {/* System Survey Status Badge & Dates */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 0.5 }}>
          <Chip
            icon={isOpen ? <OpenIcon sx={{ fontSize: 18 }} /> : <ClosedIcon sx={{ fontSize: 18 }} />}
            label={isOpen ? "ระบบเปิดรับข้อมูล (แก้ไขได้)" : "ระบบปิดรับข้อมูลแล้ว (ดูได้อย่างเดียว)"}
            color={isOpen ? "success" : "default"}
            variant="filled"
            sx={{ fontWeight: 700, fontSize: '0.85rem', px: 1, py: 2 }}
          />
          {(startDate || endDate) && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1 }}>
              🗓️ ช่วงเวลารับข้อมูล: {startDate ? formatThaiDateTime(startDate) : 'เปิดต่อเนื่อง'} ถึง {endDate ? formatThaiDateTime(endDate) : 'ไม่กำหนดสิ้นสุด'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Alert Messages */}
      {message.text && (
        <Alert 
          severity={message.type as any} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Survey Status Banner if closed */}
      {!isOpen && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>ข้อควรทราบ:</strong> ขณะนี้ระบบสำรวจความต้องการนักศึกษาฝึกประสบการณ์ <strong>ปิดรับการแก้ไขแล้ว</strong> 
          {endDate && new Date() > new Date(endDate) ? ` (สิ้นสุดระยะเวลารับข้อมูลเมื่อ ${formatThaiDateTime(endDate)})` : ''}
          {startDate && new Date() < new Date(startDate) ? ` (จะเริ่มเปิดรับข้อมูลในวันที่ ${formatThaiDateTime(startDate)})` : ''}
          {(!startDate && !endDate) || (startDate && new Date() >= new Date(startDate) && endDate && new Date() <= new Date(endDate)) ? ' (ปิดระบบโดยผู้ดูแลระบบ)' : ''}
          <br />ท่านสามารถดูข้อมูลที่บันทึกไว้ได้ หากมีความประสงค์ต้องการเปลี่ยนแปลงจำนวนนักศึกษา โปรดติดต่อฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์
        </Alert>
      )}

      {/* Filter Bar (Semester & Academic Year) */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ภาคเรียน</InputLabel>
              <Select
                value={selectedSemester}
                label="ภาคเรียน"
                onChange={(e) => handleTermChange(selectedYear, e.target.value)}
              >
                <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                <MenuItem value="3">ภาคเรียนที่ 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ปีการศึกษา</InputLabel>
              <Select
                value={selectedYear}
                label="ปีการศึกษา"
                onChange={(e) => handleTermChange(e.target.value, selectedSemester)}
              >
                {['2570', '2569', '2568', '2567', '2566'].map(yr => (
                  <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Admin School Selector */}
          {isAdminOrStaff && activeTab === 0 && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small"
                options={allSchools}
                getOptionLabel={(opt) => `${opt.name} (${opt.affiliation || 'ไม่ระบุสังกัด'})`}
                value={allSchools.find(s => s.id === selectedSchoolId) || null}
                onChange={(e, val) => {
                  if (val) {
                    setSelectedSchoolId(val.id);
                    fetchDemandForSchool(val.id, selectedYear, selectedSemester);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="เลือกโรงเรียนที่ต้องการจัดการความต้องการ" placeholder="ค้นหาชื่อโรงเรียน..." />}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Admin / Staff Primary Tabs */}
      {isAdminOrStaff && (
        <Paper sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab icon={<SurveyIcon />} iconPosition="start" label="กรอกความต้องการและบริบทรายโรงเรียน" />
            <Tab icon={<GroupIcon />} iconPosition="start" label="สรุปภาพรวมทุกโรงเรียน (Summary & Export)" />
          </Tabs>
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* TAB 0: Entry Form (Co-Teacher or Admin editing for a school) */}
      {(!isAdminOrStaff || activeTab === 0) && (
        <>
          {school ? (
            <Grid container spacing={3}>
              {/* Left Column: School Information Card & Summary */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card elevation={2} sx={{ borderRadius: 2, borderTop: '4px solid #1976d2', mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon color="primary" /> {school.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      สังกัด: {school.affiliation || 'ไม่ระบุ'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      ที่อยู่: {school.address || 'ไม่ระบุ'}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Contact Person Details */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon fontSize="small" color="action" /> ข้อมูลผู้ประสานงาน / ครูพี่เลี้ยง
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      label="ชื่อ - นามสกุล ผู้ประสานงาน"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                      disabled={!isOpen && !isAdminOrStaff}
                      placeholder="เช่น ครูสมศรี มีสุข"
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="เบอร์โทรศัพท์ติดต่อ"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      disabled={!isOpen && !isAdminOrStaff}
                      placeholder="เช่น 081-2345678"
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="หมายเหตุ / ข้อเสนอแนะเพิ่มเติม"
                      multiline
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={!isOpen && !isAdminOrStaff}
                      placeholder="เช่น ต้องการนักศึกษาช่วยสอนระดับ ม.ต้น, ประจำห้องเรียน EP หรือมีเงื่อนไขอื่นๆ"
                    />

                    {submittedByName && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
                        บันทึกล่าสุดโดย: <strong>{submittedByName}</strong>
                        {updatedAt && ` (${new Date(updatedAt).toLocaleString('th-TH')})`}
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Summary Card for Total Capacity & Quota Alert */}
                <Card elevation={2} sx={{ 
                  borderRadius: 2, 
                  bgcolor: isOverRecommendedQuota ? '#fef2f2' : 'primary.50', 
                  border: '1px solid', 
                  borderColor: isOverRecommendedQuota ? '#f87171' : 'primary.200', 
                  textAlign: 'center', 
                  p: 2 
                }}>
                  <Typography variant="subtitle2" color={isOverRecommendedQuota ? "error.main" : "primary.dark"} sx={{ fontWeight: 600 }}>
                    ยอดรวมความต้องการนักศึกษาทั้งหมด
                  </Typography>
                  <Typography variant="h3" color={isOverRecommendedQuota ? "error.main" : "primary.main"} sx={{ fontWeight: 800, my: 1 }}>
                    {totalDemand}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    คน (ภาคเรียนที่ {selectedSemester}/{selectedYear})
                  </Typography>

                  {recommendedQuota > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #cbd5e1' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: isOverRecommendedQuota ? 'error.dark' : 'text.secondary' }}>
                        โควตาสูงสุดที่แนะนำ: {recommendedQuota} คน ({currentSchoolSizeObj?.shortLabel})
                      </Typography>
                      {isOverRecommendedQuota && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>
                          ⚠️ เกินเกณฑ์โควตาตามขนาดโรงเรียน ({totalDemand - recommendedQuota} คน)
                        </Typography>
                      )}
                    </Box>
                  )}
                  {hasAnyMajorOverQuota && (
                    <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 1, fontWeight: 700, bgcolor: '#fffbeb', p: 0.5, borderRadius: 1 }}>
                      ⚠️ มีบางสาขาวิชาระบุเกิน 2 คน
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Right Column: Multi-tab Entry Form */}
              <Grid size={{ xs: 12, md: 8 }}>
                {/* Form Sub-Tabs */}
                <Paper sx={{ mb: 2, borderRadius: 2 }}>
                  <Tabs
                    value={formSubTab}
                    onChange={(e, val) => setFormSubTab(val)}
                    indicatorColor="secondary"
                    textColor="secondary"
                    variant="fullWidth"
                  >
                    <Tab 
                      icon={<GroupIcon />} 
                      iconPosition="start" 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>1. จำนวนนักศึกษาที่ต้องการรับ</span>
                          <Chip label={`${totalDemand} คน`} size="small" color={totalDemand > 0 ? "primary" : "default"} />
                        </Box>
                      } 
                    />
                    <Tab 
                      icon={<SchoolIcon />} 
                      iconPosition="start" 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>2. ข้อมูลความพร้อมสถานศึกษา</span>
                          {schoolSizeLevel && <Chip label="ระบุแล้ว" size="small" color="success" />}
                        </Box>
                      } 
                    />
                  </Tabs>
                </Paper>

                {/* Sub-Tab 0: Major Demands Table & Policy Alert */}
                {formSubTab === 0 && (
                  <Box>
                    {/* Policy and Consideration Criteria Notice Card */}
                    <Card elevation={0} sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          📌 เกณฑ์และข้อกำหนดในการจัดสรรนักศึกษาฝึกประสบการณ์วิชาชีพครู
                        </Typography>
                        <Box sx={{ color: '#0f172a', fontSize: '0.875rem', lineHeight: 1.6 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284c7' }}>
                            การพิจารณาจัดสรรนักศึกษา พิจารณาจาก:
                          </Typography>
                          <Box component="ol" sx={{ pl: 2.5, my: 0.5, fontWeight: 500 }}>
                            <li>ชั่วโมงสอนที่โรงเรียนสามารถจัดให้นักศึกษาสอนตามสาขาวิชานั้น ๆ ได้สัปดาห์ละประมาณ 6 – 8 ชั่วโมง</li>
                            <li>ครูพี่เลี้ยงตรงสาขาวิชา 1 ท่าน ต่อนักศึกษา 1 คน</li>
                          </Box>

                          <Divider sx={{ my: 1.5, borderColor: '#e0f2fe' }} />

                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#b45309', bgcolor: '#fef3c7', p: 1, borderRadius: 1, mb: 1.5 }}>
                            ⚠️ หมายเหตุ: เนื่องจากคณะครุศาสตร์ มีนักศึกษาที่สามารถออกฝึกปฏิบัติการสอนในสถานศึกษาจำนวน 15 สาขาวิชา เพื่อเปิดโอกาสให้กับสถานศึกษาทุกแห่งเป็นเครือข่ายโรงเรียนฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ จึงขอจำกัดจำนวนนักศึกษาที่จะจัดสรรให้สถานศึกษาในแต่ละสาขาวิชา <strong>ไม่เกิน 2 คน</strong> และจัดสรรตามเกณฑ์ขนาดของสถานศึกษา ดังนี้
                          </Typography>

                          <Grid container spacing={1} sx={{ pl: 0.5 }}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <strong>1. ประถมขนาดเล็ก:</strong> ไม่เกิน <strong>3 คน</strong>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <strong>2. ประถมขนาดกลาง:</strong> ไม่เกิน <strong>5 คน</strong>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <strong>3. ประถมขนาดใหญ่:</strong> ไม่เกิน <strong>10 คน</strong>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                              <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <strong>4. มัธยมขนาดเล็ก / ขยายโอกาส:</strong> ไม่เกิน <strong>10 คน</strong>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                              <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <strong>5. มัธยมขนาดกลาง - ใหญ่:</strong> ไม่เกิน <strong>15 คน</strong>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Table of Major Demands */}
                    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            ระบุจำนวนนักศึกษาที่ต้องการรับในแต่ละสาขาวิชา (15 สาขาวิชา)
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ใส่จำนวนคนตามความต้องการ (สาขาวิชาละไม่เกิน 2 คน ตามเกณฑ์คณะครุศาสตร์)
                          </Typography>
                        </Box>
                        <Chip 
                          label={`รวม: ${totalDemand} คน`} 
                          color={isOverRecommendedQuota ? "error" : "primary"} 
                          variant="filled" 
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>

                      <TableContainer sx={{ maxHeight: 520 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold', width: 60 }} align="center">ลำดับ</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>สาขาวิชา</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', width: 220 }} align="center">จำนวนที่ต้องการ (คน)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {MAJORS.map((major, idx) => {
                              const count = demands[major] || 0;
                              const isOverTwo = count > 2;
                              return (
                                <TableRow key={major} hover sx={{ bgcolor: isOverTwo ? '#fff7ed' : count > 0 ? '#f0fdf4' : 'inherit' }}>
                                  <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                    {idx + 1}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: count > 0 ? 700 : 500, fontSize: '0.9rem' }}>
                                    {major}
                                    {isOverTwo && (
                                      <Chip 
                                        label="เกินเกณฑ์ 2 คน/สาขา" 
                                        size="small" 
                                        color="warning" 
                                        sx={{ ml: 1.5, fontSize: '0.7rem', height: 20 }} 
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                      <TextField
                                        size="small"
                                        type="number"
                                        value={count}
                                        onChange={(e) => handleMajorCountChange(major, e.target.value)}
                                        disabled={!isOpen && !isAdminOrStaff}
                                        slotProps={{
                                          htmlInput: {
                                            min: 0,
                                            max: 99,
                                            style: { textAlign: 'center', fontWeight: 700, fontSize: '0.95rem', padding: '6px 8px' }
                                          }
                                        }}
                                        sx={{ width: 80 }}
                                      />
                                      <Typography variant="caption" color="textSecondary">คน</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Save Button Footer */}
                      <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Button 
                          variant="outlined" 
                          color="secondary"
                          onClick={() => setFormSubTab(1)}
                        >
                          ถัดไป: กรอกข้อมูลความพร้อมสถานศึกษา ➡️
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<SaveIcon />}
                          onClick={handleSave}
                          disabled={saving || (!isOpen && !isAdminOrStaff)}
                          sx={{ px: 4, py: 1, fontWeight: 700 }}
                        >
                          {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูลทั้งหมด'}
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Sub-Tab 1: School Readiness Profile & Context (From Word Document) */}
                {formSubTab === 1 && (
                  <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <SchoolIcon /> ข้อมูลความพร้อมและบริบทสถานศึกษาเพื่อใช้เป็นเกณฑ์จัดสรร
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 3 }}>
                      ข้อมูลตามแบบฟอร์มแสดงความจำนงการเข้าร่วมเป็นเครือข่ายโรงเรียนฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มรภ.กำแพงเพชร
                    </Typography>

                    <Grid container spacing={3}>
                      {/* 1. ประวัติการเข้าร่วม */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          1. เคยเข้าร่วมเป็นเครือข่ายโรงเรียนฝึกประสบการณ์วิชาชีพครูหรือไม่
                        </Typography>
                        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                          <Grid size={{ xs: 12, sm: 5 }}>
                            <FormControl fullWidth size="small">
                              <Select
                                value={hasPreviousExperience}
                                onChange={(e) => setHasPreviousExperience(e.target.value)}
                                disabled={!isOpen && !isAdminOrStaff}
                              >
                                <MenuItem value="เคย">เคยเข้าร่วม</MenuItem>
                                <MenuItem value="ไม่เคย">ไม่เคยเข้าร่วม</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {hasPreviousExperience === 'เคย' && (
                            <Grid size={{ xs: 12, sm: 7 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="ปีการศึกษา (ครั้งสุดท้ายที่มีนักศึกษาฝึกสอน)"
                                placeholder="เช่น 2568"
                                value={lastParticipatedYear}
                                onChange={(e) => setLastParticipatedYear(e.target.value)}
                                disabled={!isOpen && !isAdminOrStaff}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>

                      <Grid size={{ xs: 12 }}><Divider /></Grid>

                      {/* 2. ลักษณะและขนาดของโรงเรียน */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          2. ลักษณะของโรงเรียน (ตามเกณฑ์จัดสรรคณะครุศาสตร์)
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>เลือกลักษณะและขนาดของโรงเรียน</InputLabel>
                          <Select
                            value={schoolSizeLevel}
                            label="เลือกลักษณะและขนาดของโรงเรียน"
                            onChange={(e) => setSchoolSizeLevel(e.target.value)}
                            disabled={!isOpen && !isAdminOrStaff}
                          >
                            <MenuItem value="">-- กรุณาเลือกลักษณะขนาดโรงเรียน --</MenuItem>
                            {SCHOOL_SIZES.map(s => (
                              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12 }}><Divider /></Grid>

                      {/* 3. ระยะทางจากมหาวิทยาลัย */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          3. ระยะทางจากมหาวิทยาลัยถึงโรงเรียน
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="ระยะทาง (กิโลเมตร)"
                          placeholder="เช่น 15.5"
                          value={distanceFromUniversityKm}
                          onChange={(e) => setDistanceFromUniversityKm(e.target.value)}
                          disabled={!isOpen && !isAdminOrStaff}
                          slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
                        />
                      </Grid>

                      {/* 4. สวัสดิการสำหรับนักศึกษา */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          4. สวัสดิการสำหรับนักศึกษาปฏิบัติการสอน
                        </Typography>
                        <Grid container spacing={2}>
                          {/* Accommodation */}
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox 
                                    checked={hasAccommodation === 1} 
                                    onChange={(e) => setHasAccommodation(e.target.checked ? 1 : 0)} 
                                    disabled={!isOpen && !isAdminOrStaff} 
                                  />
                                }
                                label={<strong>มีที่พักสำหรับนักศึกษา</strong>}
                              />
                              {hasAccommodation === 1 && (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="รายละเอียดที่พัก (เช่น ที่พักภายในโรงเรียน, แยกชาย-หญิง)"
                                  value={accommodationDetails}
                                  onChange={(e) => setAccommodationDetails(e.target.value)}
                                  disabled={!isOpen && !isAdminOrStaff}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Box>
                          </Grid>

                          {/* Lunch */}
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox 
                                    checked={hasLunch === 1} 
                                    onChange={(e) => setHasLunch(e.target.checked ? 1 : 0)} 
                                    disabled={!isOpen && !isAdminOrStaff} 
                                  />
                                }
                                label={<strong>มีอาหารกลางวัน</strong>}
                              />
                            </Box>
                          </Grid>

                          {/* Other welfare */}
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="สวัสดิการอื่นๆ (เช่น เบี้ยเลี้ยง, อุปกรณ์การสอน, รถรับส่ง)"
                              placeholder="ระบุสวัสดิการอื่นๆ ที่โรงเรียนจัดให้แก่นักศึกษา (ถ้ามี)"
                              value={welfareNotes}
                              onChange={(e) => setWelfareNotes(e.target.value)}
                              disabled={!isOpen && !isAdminOrStaff}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid size={{ xs: 12 }}><Divider /></Grid>

                      {/* 5. จำนวนบุคลากรและนักเรียน */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          5. จำนวนบุคลากรและนักเรียน
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="จำนวนครูทั้งหมด (คน)"
                              value={teacherCountTotal}
                              onChange={(e) => setTeacherCountTotal(e.target.value)}
                              disabled={!isOpen && !isAdminOrStaff}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="ครูชาย (คน)"
                              value={teacherCountMale}
                              onChange={(e) => setTeacherCountMale(e.target.value)}
                              disabled={!isOpen && !isAdminOrStaff}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, sm: 4 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="ครูหญิง (คน)"
                              value={teacherCountFemale}
                              onChange={(e) => setTeacherCountFemale(e.target.value)}
                              disabled={!isOpen && !isAdminOrStaff}
                            />
                          </Grid>

                          {/* Education levels */}
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
                              การจัดการศึกษาระดับ (เลือกทุกระดับที่เปิดสอน):
                            </Typography>
                            <FormGroup row>
                              {EDUCATION_LEVEL_OPTIONS.map(opt => (
                                <FormControlLabel
                                  key={opt}
                                  control={
                                    <Checkbox
                                      checked={educationLevels.includes(opt)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setEducationLevels([...educationLevels, opt]);
                                        } else {
                                          setEducationLevels(educationLevels.filter(x => x !== opt));
                                        }
                                      }}
                                      disabled={!isOpen && !isAdminOrStaff}
                                    />
                                  }
                                  label={<Typography variant="body2">{opt}</Typography>}
                                  sx={{ minWidth: 260 }}
                                />
                              ))}
                            </FormGroup>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="จำนวนนักเรียนรวมทั้งหมด (คน)"
                              value={studentCountTotal}
                              onChange={(e) => setStudentCountTotal(e.target.value)}
                              disabled={!isOpen && !isAdminOrStaff}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid size={{ xs: 12 }}><Divider /></Grid>

                      {/* 6. ผลการประเมินคุณภาพ จาก สมศ. */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          6. ผลการประเมินคุณภาพภายนอก จาก สมศ.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>รอบการประเมิน</InputLabel>
                              <Select
                                value={onesqaRound}
                                label="รอบการประเมิน"
                                onChange={(e) => setOnesqaRound(e.target.value)}
                                disabled={!isOpen && !isAdminOrStaff}
                              >
                                <MenuItem value="รอบที่ 2">รอบที่ 2</MenuItem>
                                <MenuItem value="รอบที่ 3">รอบที่ 3</MenuItem>
                                <MenuItem value="รอบที่ 4">รอบที่ 4</MenuItem>
                                <MenuItem value="รอบที่ 5">รอบที่ 5</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>ระดับการศึกษาปฐมวัย</InputLabel>
                              <Select
                                value={onesqaEarlyChildhood}
                                label="ระดับการศึกษาปฐมวัย"
                                onChange={(e) => setOnesqaEarlyChildhood(e.target.value)}
                                disabled={!isOpen && !isAdminOrStaff}
                              >
                                {ONESQA_RATINGS.map(r => (
                                  <MenuItem key={r} value={r}>{r}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>ระดับประถมศึกษา</InputLabel>
                              <Select
                                value={onesqaPrimary}
                                label="ระดับประถมศึกษา"
                                onChange={(e) => setOnesqaPrimary(e.target.value)}
                                disabled={!isOpen && !isAdminOrStaff}
                              >
                                {ONESQA_RATINGS.map(r => (
                                  <MenuItem key={r} value={r}>{r}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>ระดับมัธยมศึกษา</InputLabel>
                              <Select
                                value={onesqaSecondary}
                                label="ระดับมัธยมศึกษา"
                                onChange={(e) => setOnesqaSecondary(e.target.value)}
                                disabled={!isOpen && !isAdminOrStaff}
                              >
                                {ONESQA_RATINGS.map(r => (
                                  <MenuItem key={r} value={r}>{r}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid size={{ xs: 12 }}><Divider /></Grid>

                      {/* 7. ข้อมูลความพร้อมเป็นครูพี่เลี้ยง */}
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            7. ข้อมูลความพร้อมเป็นครูพี่เลี้ยง (รายชื่อครูที่พร้อมรับนักศึกษา)
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddMentorRow}
                            disabled={!isOpen && !isAdminOrStaff}
                          >
                            เพิ่มครูพี่เลี้ยง
                          </Button>
                        </Box>

                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 320 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 40 }} align="center">ที่</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>ชื่อ – สกุล</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>วุฒิการศึกษา (วิชาเอก)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>วิทยฐานะ</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>วันบรรจุ</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 70 }}>อายุงาน(ปี)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>วิชาที่สอน</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 90 }}>ระดับชั้น</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 45 }} align="center"></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {mentorList.map((m, idx) => (
                                <TableRow key={idx}>
                                  <TableCell align="center">{idx + 1}</TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={m.name}
                                      onChange={(e) => handleMentorChange(idx, 'name', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                      placeholder="เช่น นางสมศรี สุขใจ"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={m.degree}
                                      onChange={(e) => handleMentorChange(idx, 'degree', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                      placeholder="เช่น ค.บ.ภาษาไทย"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={m.academic_standing}
                                      onChange={(e) => handleMentorChange(idx, 'academic_standing', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                      placeholder="เช่น ชำนาญการ"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={m.appointed_date}
                                      onChange={(e) => handleMentorChange(idx, 'appointed_date', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                      placeholder="วัน/ด/ป"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      type="number"
                                      value={m.experience_years}
                                      onChange={(e) => handleMentorChange(idx, 'experience_years', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={m.subject}
                                      onChange={(e) => handleMentorChange(idx, 'subject', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                      placeholder="เช่น ภาษาไทย"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={m.grade_level}
                                      onChange={(e) => handleMentorChange(idx, 'grade_level', e.target.value)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                      placeholder="เช่น ป.4-6"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveMentorRow(idx)}
                                      disabled={!isOpen && !isAdminOrStaff}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {mentorList.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                                    ยังไม่มีรายชื่อครูพี่เลี้ยง (กดปุ่ม &quot;เพิ่มครูพี่เลี้ยง&quot; เพื่อระบุรายชื่อครูที่มีความพร้อม)
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>

                      {/* Bottom action buttons */}
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                          <Button 
                            variant="outlined"
                            onClick={() => setFormSubTab(0)}
                          >
                            ⬅️ ย้อนกลับไป: ระบุจำนวนนักศึกษาตามสาขา
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || (!isOpen && !isAdminOrStaff)}
                            sx={{ px: 4, py: 1, fontWeight: 700 }}
                          >
                            {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูลทั้งหมด'}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              กรุณาเลือกโรงเรียนที่ต้องการระบุข้อมูลความต้องการนักศึกษา
            </Alert>
          )}
        </>
      )}

      {/* TAB 1: Summary Table & Excel Export (Admin / Staff) */}
      {isAdminOrStaff && activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                สรุปความต้องการนักศึกษาและข้อมูลความพร้อมทุกโรงเรียน (ภาคเรียนที่ {selectedSemester}/{selectedYear})
              </Typography>
              <Typography variant="caption" color="textSecondary">
                โรงเรียนทั้งหมด: {summaryData.length} โรงเรียน | ยอดรวมความต้องการทั่วทั้งระบบ: {summaryData.reduce((acc, curr) => acc + (curr.effective_total || 0), 0)} คน
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportSummaryExcel}
              disabled={summaryData.length === 0}
            >
              ส่งออก Excel (.xlsx)
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto', borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 45 }} align="center">ที่</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>ชื่อโรงเรียน</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>สังกัด</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>ขนาดสถานศึกษา</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 90 }}>ระยะทาง (กม.)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>ที่พัก</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 90 }}>อาหารกลางวัน</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>ผู้ประสานงาน / ครูพี่เลี้ยง</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>เบอร์ติดต่อ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e3f2fd', minWidth: 100 }}>
                    รวมความต้องการ
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>สถานะ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.map((row, index) => {
                  const hasSubmitted = row.has_submitted;
                  const sizeObj = SCHOOL_SIZES.find(s => s.value === row.school_size_level);
                  return (
                    <TableRow key={row.school_id} hover>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {row.school_name}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {row.affiliation || '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {sizeObj ? (
                          <Chip label={sizeObj.shortLabel} size="small" variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                        {row.distance_from_university_km !== null && row.distance_from_university_km !== undefined ? `${row.distance_from_university_km} กม.` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {row.has_accommodation ? (
                          <Chip label="มีที่พัก" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.has_lunch ? (
                          <Chip label="มี" size="small" color="info" sx={{ fontSize: '0.7rem' }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {row.contact_person || row.submitted_by_name || '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {row.contact_phone || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#f0f9ff' }}>
                        <Chip
                          label={`${row.effective_total || 0} คน`}
                          color={row.effective_total > 0 ? "primary" : "default"}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={hasSubmitted ? "บันทึกแล้ว" : "ค่าตั้งต้น"}
                          color={hasSubmitted ? "success" : "default"}
                          size="small"
                          variant={hasSubmitted ? "filled" : "outlined"}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {summaryData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      ไม่พบข้อมูลโรงเรียนในระบบ
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default StudentDemandSurvey;
